/**
 * standupBoot — the wiring behind the morning standup (src/lib/agent/standup.ts).
 *
 * Three responsibilities, all deliberately thin so every judgement stays in
 * already-tested engines:
 *
 *   • gatherStandupInputs(): REAL counts only — drafted emails come from the
 *     draft-first composers (draftForNudge over the live nudge sweep),
 *     prepped posts from the preppedDrafts generator over the live Brain,
 *     proposals from the approvals inbox. Nothing is fabricated; an empty
 *     workspace gathers zeros and composeStandup returns null.
 *
 *   • lastStandupShownAt persistence: one synthetic org-scoped row in the
 *     existing `brainFacts` collection with a deterministic id — the exact
 *     mechanism onboardingCompleted uses (src/lib/onboarding.ts). No new
 *     collection, works on localStorage today and Firestore unchanged.
 *
 *   • approveAllReversible(): the same batch mechanic as the Dashboard's
 *     "Approve all reversible (N)" — sequential resolveApproval over the
 *     pending proposals whose actionRisk is 'auto', so each action commits
 *     (and logs) before the next runs. Gated proposals are never touched.
 */

import { db, nowIso } from '../db/store';
import type { BrainFact } from '../db/types';
import { listApprovals, resolveApproval } from './approvals';
import { actionRisk } from './actions';
import { scanSystem } from './systemScanner';
import { proactiveNudges, type Nudge } from './proactiveNudges';
import { draftForNudge } from './draftFirst';
import { deriveRecentWins, preparedDrafts } from '../content/preppedDrafts';
import type { StandupInputs } from './standup';

// --- lastStandupShownAt — persisted like onboardingCompleted ---------------

/** Synthetic account id — the standup is org-wide, not per-account. */
export const STANDUP_ACCOUNT_ID = '__org__';

/** Deterministic doc id so every write upserts the one stamp row. */
export const STANDUP_FLAG_FACT_ID = `${STANDUP_ACCOUNT_ID}:rules:lastStandupShownAt`;

/** ISO timestamp of the last standup shown, or null when never shown. */
export async function readLastStandupShownAt(): Promise<string | null> {
  const fact = await db.get('brainFacts', STANDUP_FLAG_FACT_ID);
  return fact?.value ?? null;
}

/**
 * Stamp the standup as shown NOW (or at an injected time, for tests).
 * Called on ANY of the three chips and on manual close — once stamped,
 * shouldShowStandup suppresses the rest of the calendar day.
 */
export async function markStandupShown(at: string = nowIso()): Promise<void> {
  const existing = await db.get('brainFacts', STANDUP_FLAG_FACT_ID);
  const fact: BrainFact = {
    id: STANDUP_FLAG_FACT_ID,
    accountId: STANDUP_ACCOUNT_ID,
    section: 'rules',
    field: 'lastStandupShownAt',
    label: 'Morning standup last shown',
    value: at,
    source: 'operator',
    confidence: 1,
    version: (existing?.version ?? 0) + 1,
    setBy: 'operator',
    setAt: nowIso(),
  };
  await db.put('brainFacts', fact);
}

// --- lastSeenFeedAt — the "while you were away" baseline --------------------

/** Deterministic doc id for the feed-seen stamp (same brainFacts mechanic). */
export const FEED_SEEN_FACT_ID = `${STANDUP_ACCOUNT_ID}:rules:lastSeenFeedAt`;

/** ISO timestamp the operator last saw the feed, or null when never. */
export async function readLastSeenFeedAt(): Promise<string | null> {
  const fact = await db.get('brainFacts', FEED_SEEN_FACT_ID);
  return fact?.value ?? null;
}

/**
 * Stamp the feed as seen NOW (or at an injected time, for tests). Called
 * when a session greeting is settled — the next session's "While you were
 * away" digest diffs from here.
 */
export async function markFeedSeen(at: string = nowIso()): Promise<void> {
  const existing = await db.get('brainFacts', FEED_SEEN_FACT_ID);
  const fact: BrainFact = {
    id: FEED_SEEN_FACT_ID,
    accountId: STANDUP_ACCOUNT_ID,
    section: 'rules',
    field: 'lastSeenFeedAt',
    label: 'Agent feed last seen',
    value: at,
    source: 'operator',
    confidence: 1,
    version: (existing?.version ?? 0) + 1,
    setBy: 'operator',
    setAt: nowIso(),
  };
  await db.put('brainFacts', fact);
}

// --- Real counts ------------------------------------------------------------

/** Everything the session greeting needs from the live store, in one pass. */
export interface SessionContext {
  /** The standup's real counts (same shape gatherStandupInputs returns). */
  inputs: Omit<StandupInputs, 'userName'>;
  /** The live proactive-nudge sweep, best first — greeting chips. */
  topNudges: Nudge[];
  /** True only for a genuinely cold store: zero people AND zero accounts
   *  (the FirstRunWizard's own definition of "truly cold"). */
  workspaceEmpty: boolean;
}

/**
 * Gather the standup's counts from the live store — the SAME engines every
 * other surface uses, so the standup can never disagree with Home or the
 * Content Engine about what exists. Async and off the render path; the app
 * never blocks on it.
 */
export async function gatherStandupInputs(
  now: number = Date.now(),
): Promise<Omit<StandupInputs, 'userName'>> {
  return (await gatherSessionContext(now)).inputs;
}

/**
 * Gather the FULL session-greeting context in one store pass: the standup
 * counts (exactly what gatherStandupInputs returns), the live nudge sweep
 * (greeting chips), and the honest workspace-empty flag for the cold-start
 * ask. Same engines as every other surface — the greeting can never
 * disagree with Home or the strips about what exists.
 */
export async function gatherSessionContext(
  now: number = Date.now(),
): Promise<SessionContext> {
  const [people, tasks, tickets, accounts, deliverables, brainFacts, contentItems, pending] =
    await Promise.all([
      db.list('people'),
      db.list('tasks'),
      db.list('tickets'),
      db.list('accounts'),
      db.list('deliverables'),
      db.list('brainFacts'),
      db.list('contentItems'),
      listApprovals('pending'),
    ]);

  // Drafted emails: nudges whose record composes into a FINISHED artifact
  // with a resolvable recipient (state 'draft') — the exact rule the
  // "Argo suggests" strips and Home do-now cards apply.
  const { findings } = scanSystem({ tickets, tasks, people, accounts });
  const nudges = proactiveNudges({ people, tasks, findings });
  const drafts = nudges.filter(
    (n) => draftForNudge(n, people, now)?.state === 'draft',
  ).length;

  // Prepped posts: the Content Engine's deterministic generator over the
  // real Brain. Synthetic org-scoped settings rows (onboarding/standup
  // stamps) are NOT Brain content and must not count as grounding.
  const realFacts = brainFacts.filter((f) => f.accountId !== STANDUP_ACCOUNT_ID);
  const preppedPosts = preparedDrafts(
    realFacts,
    people,
    deriveRecentWins(deliverables, people),
    contentItems,
  ).length;

  return {
    inputs: {
      drafts,
      preppedPosts,
      pendingApprovals: pending.length,
      reversibleApprovals: pending.filter((a) => actionRisk(a.action) === 'auto').length,
    },
    topNudges: nudges,
    workspaceEmpty: people.length === 0 && accounts.length === 0,
  };
}

// --- Batch approve — the Dashboard's "Approve all reversible" mechanic -----

/**
 * Approve every pending reversible ('auto' risk) proposal, sequentially —
 * same discipline as the Dashboard panel, so each action commits and logs
 * before the next runs. Gated proposals stay for manual review. Returns
 * how many were approved.
 */
export async function approveAllReversible(): Promise<number> {
  const pending = await listApprovals('pending');
  const reversible = pending.filter((a) => actionRisk(a.action) === 'auto');
  for (const a of reversible) {
    await resolveApproval(a.id, 'approved');
  }
  return reversible.length;
}
